import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Image, Layers, Inbox, LogOut, Trash2, Upload, Plus, Pencil, Check, X, Phone, Mail, Copy, ChevronDown, Users, Download, ScanLine, Camera, KeyRound } from "lucide-react";
import {
  getMe, clearToken, hasToken,
  getPortfolio, addPortfolio, deletePortfolio, fileUrl,
  getServices, addService, updateService, deleteService,
  getInquiries, deleteInquiry,
  createInvitation, getInvitations, getInvitationRsvps, deleteInvitation,
} from "@/lib/api";
import { LOGO_ICON, BRAND } from "@/lib/contact";
import { INVITE_THEMES } from "@/data/inviteThemes";

const CATS = ["زفاف", "تخرّج", "رسمي", "مناسبات خاصة", "كروت إلكترونية"];
const TABS = [
  { id: "gallery", label: "المعرض", icon: Image },
  { id: "invitations", label: "الدعوات", icon: Mail },
  { id: "checkin", label: "الاستقبال", icon: ScanLine },
  { id: "services", label: "الخدمات", icon: Layers },
  { id: "orders", label: "الطلبات", icon: Inbox },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("gallery");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!hasToken()) return navigate("/admin/login", { replace: true });
    getMe().then(() => setReady(true)).catch(() => { clearToken(); navigate("/admin/login", { replace: true }); });
  }, [navigate]);

  const logout = () => { clearToken(); navigate("/admin/login", { replace: true }); };

  if (!ready) return <div className="min-h-screen gradient-primary flex items-center justify-center text-white font-serif text-2xl">جارٍ التحميل...</div>;

  return (
    <div className="min-h-screen bg-ivory" dir="rtl" data-testid="admin-dashboard">
      <header className="gradient-primary text-white grain">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between relative z-10">
          <div className="flex items-center gap-3">
            <img src={LOGO_ICON} alt={BRAND} className="w-11 h-11 rounded-lg object-cover ring-1 ring-gold/50" />
            <div>
              <div className="font-serif text-2xl font-bold leading-none">{BRAND}</div>
              <div className="text-xs text-white/60 mt-1">لوحة التحكم</div>
            </div>
          </div>
          <button onClick={logout} className="flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-sm hover:border-gold hover:text-gold transition" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> خروج
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 -mt-6 relative z-20">
        <div className="bg-white rounded-2xl border border-lav p-2 flex gap-2 shadow-lg">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-medium transition ${tab === t.id ? "gradient-primary text-white" : "hover:bg-lavender"}`} style={tab === t.id ? {} : { color: "#6c7189" }} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {tab === "gallery" && <GalleryTab />}
        {tab === "invitations" && <InvitationsTab />}
        {tab === "checkin" && <ReceptionTab />}
        {tab === "services" && <ServicesTab />}
        {tab === "orders" && <OrdersTab />}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <h2 className="font-serif text-2xl font-bold mb-6" style={{ color: "#232a44" }}>{children}</h2>;
}

function GalleryTab() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState(CATS[0]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => getPortfolio().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await addPortfolio(file, cat);
      toast.success("تمت إضافة العمل");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch {
      toast.error("تعذّر رفع الصورة");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try { await deletePortfolio(id); toast.success("تم الحذف"); load(); } catch { toast.error("تعذّر الحذف"); }
  };

  return (
    <div>
      <SectionTitle>معرض الأعمال</SectionTitle>
      <div className="bg-white rounded-2xl border border-lav p-6 mb-8">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>نوع المناسبة</label>
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="rounded-xl border border-lav px-4 py-2.5 text-right outline-none focus:border-primary" data-testid="upload-category-select">
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" data-testid="upload-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 rounded-xl gradient-primary text-white px-6 py-2.5 font-medium disabled:opacity-60" data-testid="upload-trigger-btn">
            <Upload className="w-4 h-4" /> {uploading ? "جارٍ الرفع..." : "رفع صورة عمل"}
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 text-sm" style={{ color: "#6c7189" }}>لا توجد أعمال بعد — ارفع أول صورة.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="gallery-grid">
          {items.map((it) => (
            <div key={it.id} className="group relative rounded-2xl overflow-hidden border border-lav bg-white" data-testid={`gallery-item-${it.id}`}>
              <img src={fileUrl(it.image_path)} alt={it.category} className="w-full aspect-[4/5] object-cover" />
              <span className="absolute top-3 right-3 text-[11px] rounded-full bg-white/90 px-3 py-1 font-medium" style={{ color: "#3a4a78" }}>{it.category}</span>
              <button onClick={() => remove(it.id)} className="absolute bottom-3 left-3 w-9 h-9 rounded-full bg-red-500 text-white flex items-center justify-center opacity-100 md:opacity-0 md:group-hover:opacity-100 transition" data-testid={`delete-gallery-${it.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ServicesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", desc: "" });

  const load = () => getServices().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const startAdd = () => { setEditing("new"); setForm({ title: "", desc: "" }); };
  const startEdit = (s) => { setEditing(s.id); setForm({ title: s.title, desc: s.desc }); };
  const cancel = () => { setEditing(null); setForm({ title: "", desc: "" }); };

  const save = async () => {
    if (!form.title.trim()) return toast.error("أدخل عنوان الخدمة");
    try {
      if (editing === "new") await addService(form);
      else await updateService(editing, form);
      toast.success("تم الحفظ");
      cancel(); load();
    } catch { toast.error("تعذّر الحفظ"); }
  };

  const remove = async (id) => {
    try { await deleteService(id); toast.success("تم الحذف"); load(); } catch { toast.error("تعذّر الحذف"); }
  };

  const inputCls = "w-full rounded-xl border border-lav px-4 py-2.5 text-right outline-none focus:border-primary";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <SectionTitle>الخدمات</SectionTitle>
        {editing === null && (
          <button onClick={startAdd} className="flex items-center gap-2 rounded-full gradient-primary text-white px-5 py-2.5 text-sm font-medium" data-testid="add-service-btn">
            <Plus className="w-4 h-4" /> خدمة جديدة
          </button>
        )}
      </div>

      {editing === "new" && (
        <div className="bg-white rounded-2xl border border-lav p-6 mb-6" data-testid="service-form">
          <div className="space-y-4">
            <input className={inputCls} placeholder="عنوان الخدمة" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="service-title-input" />
            <textarea rows={2} className={inputCls} placeholder="وصف قصير" value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} data-testid="service-desc-input" />
            <div className="flex gap-3">
              <button onClick={save} className="flex items-center gap-2 rounded-xl gradient-primary text-white px-5 py-2.5 text-sm" data-testid="save-service-btn"><Check className="w-4 h-4" /> حفظ</button>
              <button onClick={cancel} className="flex items-center gap-2 rounded-xl border border-lav px-5 py-2.5 text-sm" style={{ color: "#6c7189" }}><X className="w-4 h-4" /> إلغاء</button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.map((s) => (
          <div key={s.id} className="bg-white rounded-2xl border border-lav p-5" data-testid={`service-row-${s.id}`}>
            {editing === s.id ? (
              <div className="space-y-4">
                <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="service-title-input" />
                <textarea rows={2} className={inputCls} value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} data-testid="service-desc-input" />
                <div className="flex gap-3">
                  <button onClick={save} className="flex items-center gap-2 rounded-xl gradient-primary text-white px-5 py-2.5 text-sm" data-testid="save-service-btn"><Check className="w-4 h-4" /> حفظ</button>
                  <button onClick={cancel} className="flex items-center gap-2 rounded-xl border border-lav px-5 py-2.5 text-sm" style={{ color: "#6c7189" }}><X className="w-4 h-4" /> إلغاء</button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-serif text-lg font-semibold" style={{ color: "#232a44" }}>{s.title}</h3>
                  <p className="text-sm mt-1" style={{ color: "#6c7189" }}>{s.desc}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => startEdit(s)} className="w-9 h-9 rounded-lg bg-lavender flex items-center justify-center text-primary" data-testid={`edit-service-${s.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(s.id)} className="w-9 h-9 rounded-lg bg-red-50 text-red-500 flex items-center justify-center" data-testid={`delete-service-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersTab() {
  const [items, setItems] = useState([]);
  const load = () => getInquiries().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await deleteInquiry(id); toast.success("تم الحذف"); load(); } catch { toast.error("تعذّر الحذف"); }
  };

  return (
    <div>
      <SectionTitle>طلبات العملاء ({items.length})</SectionTitle>
      {items.length === 0 ? (
        <div className="text-center py-16 text-sm" style={{ color: "#6c7189" }}>لا توجد طلبات بعد.</div>
      ) : (
        <div className="space-y-3" data-testid="orders-list">
          {items.map((o) => (
            <div key={o.id} className="bg-white rounded-2xl border border-lav p-5 flex items-start justify-between gap-4" data-testid={`order-row-${o.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-semibold" style={{ color: "#232a44" }}>{o.name}</h3>
                  {o.event_type && <span className="text-[11px] rounded-full bg-lavender px-3 py-1" style={{ color: "#3a4a78" }}>{o.event_type}</span>}
                </div>
                {o.phone && <a href={`tel:${o.phone}`} className="flex items-center gap-1.5 text-sm mt-2 text-primary"><Phone className="w-3.5 h-3.5" /> {o.phone}</a>}
                {o.message && <p className="text-sm mt-2" style={{ color: "#6c7189" }}>{o.message}</p>}
                <div className="text-xs mt-2" style={{ color: "#9aa0b4" }}>{new Date(o.created_at).toLocaleString("ar")}</div>
              </div>
              <button onClick={() => remove(o.id)} className="w-9 h-9 rounded-lg bg-red-50 text-red-500 flex items-center justify-center shrink-0" data-testid={`delete-order-${o.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


const EVENT_TYPES = [
  { id: "wedding", label: "زفاف" },
  { id: "graduation", label: "تخرّج" },
  { id: "corporate", label: "مناسبة رسمية" },
  { id: "special", label: "مناسبة خاصة" },
];

function InvitationsTab() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [rsvps, setRsvps] = useState({});
  const empty = { event_type: "wedding", title: "", hosts: "", host_gender: "male", event_date: "", venue_name: "", map_query: "", allowed_guests: 1, message: "", template_id: "royal" };
  const [form, setForm] = useState(empty);

  const load = () => getInvitations().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim()) return toast.error("أدخل عنوان المناسبة");
    if (!form.event_date) return toast.error("أدخل تاريخ ووقت المناسبة");
    try {
      await createInvitation({ ...form, event_date: new Date(form.event_date).toISOString(), allowed_guests: parseInt(form.allowed_guests, 10) || 1 });
      toast.success("تم إنشاء الدعوة");
      setForm(empty); setCreating(false); load();
    } catch { toast.error("تعذّر الإنشاء"); }
  };

  const remove = async (id) => {
    try { await deleteInvitation(id); toast.success("تم الحذف"); load(); } catch { toast.error("تعذّر الحذف"); }
  };

  const toggle = async (id) => {
    if (expanded === id) return setExpanded(null);
    setExpanded(id);
    if (!rsvps[id]) {
      try { const data = await getInvitationRsvps(id); setRsvps((r) => ({ ...r, [id]: data })); } catch {}
    }
  };

  const copyLink = (id) => {
    navigator.clipboard.writeText(`${window.location.origin}/invite/${id}`);
    toast.success("تم نسخ رابط الدعوة");
  };

  const exportCsv = async (inv) => {
    let list;
    try { list = await getInvitationRsvps(inv.id); setRsvps((r) => ({ ...r, [inv.id]: list })); } catch { return toast.error("تعذّر جلب البيانات"); }
    const accepted = (list || []).filter((r) => r.status === "accepted");
    if (accepted.length === 0) return toast.error("لا يوجد حضور مؤكّد بعد");
    const rows = [["الاسم", "عدد المدعوين", "رمز البطاقة", "تاريخ التأكيد"]];
    accepted.forEach((r) => rows.push([r.guest_name, r.guests_count, r.pass_code || "", new Date(r.created_at).toLocaleString("ar")]));
    rows.push(["الإجمالي", accepted.reduce((s, r) => s + (r.guests_count || 0), 0), "", ""]);
    const csv = "\uFEFF" + rows.map((row) => row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-${inv.title || inv.id}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
    toast.success("تم تصدير قائمة الحضور");
  };

  const inputCls = "w-full rounded-xl border border-lav px-4 py-2.5 text-right outline-none focus:border-primary";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <SectionTitle>الدعوات الإلكترونية</SectionTitle>
        {!creating && (
          <button onClick={() => setCreating(true)} className="flex items-center gap-2 rounded-full gradient-primary text-white px-5 py-2.5 text-sm font-medium" data-testid="add-invitation-btn">
            <Plus className="w-4 h-4" /> دعوة جديدة
          </button>
        )}
      </div>

      {creating && (
        <div className="bg-white rounded-2xl border border-lav p-6 mb-6" data-testid="invitation-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>نوع المناسبة</label>
              <select className={inputCls} value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} data-testid="inv-type-select">
                {EVENT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>عنوان المناسبة *</label>
              <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="زفاف أحمد وسارة" data-testid="inv-title-input" />
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>أسماء المُضيفين</label>
              <input className={inputCls} value={form.hosts} onChange={(e) => setForm({ ...form, hosts: e.target.value })} placeholder="عائلة العبدالله" data-testid="inv-hosts-input" />
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>جنس الداعي</label>
              <select className={inputCls} value={form.host_gender} onChange={(e) => setForm({ ...form, host_gender: e.target.value })} data-testid="inv-gender-select">
                <option value="male">ذكر — يتشرّف</option>
                <option value="female">أنثى — تتشرّف</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>التاريخ والوقت *</label>
              <input type="datetime-local" className={inputCls} value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="inv-date-input" />
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>اسم القاعة / المكان</label>
              <input className={inputCls} value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })} placeholder="قاعة الماسة" data-testid="inv-venue-input" />
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>موقع الخريطة (بحث Google)</label>
              <input className={inputCls} value={form.map_query} onChange={(e) => setForm({ ...form, map_query: e.target.value })} placeholder="قاعة الماسة الرياض" data-testid="inv-map-input" />
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>عدد المدعوين لكل بطاقة *</label>
              <input type="number" min="1" className={inputCls} value={form.allowed_guests} onChange={(e) => setForm({ ...form, allowed_guests: e.target.value })} data-testid="inv-guests-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>رسالة للضيوف</label>
              <textarea rows={2} className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="يشرفنا حضوركم..." data-testid="inv-message-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm mb-2" style={{ color: "#6c7189" }}>لون بطاقة الدعوة</label>
              <div className="flex flex-wrap gap-3" data-testid="inv-theme-picker">
                {INVITE_THEMES.map((t) => (
                  <button key={t.id} type="button" onClick={() => setForm({ ...form, template_id: t.id })} className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 transition ${form.template_id === t.id ? "border-primary" : "border-lav"}`} data-testid={`inv-theme-${t.id}`}>
                    <span className="w-6 h-6 rounded-md" style={{ background: t.bg }} />
                    <span className="text-sm" style={{ color: "#232a44" }}>{t.label}</span>
                    {form.template_id === t.id && <Check className="w-4 h-4 text-primary" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={save} className="flex items-center gap-2 rounded-xl gradient-primary text-white px-6 py-2.5 text-sm" data-testid="save-invitation-btn"><Check className="w-4 h-4" /> إنشاء الدعوة</button>
            <button onClick={() => { setCreating(false); setForm(empty); }} className="flex items-center gap-2 rounded-xl border border-lav px-6 py-2.5 text-sm" style={{ color: "#6c7189" }}><X className="w-4 h-4" /> إلغاء</button>
          </div>
        </div>
      )}

      {items.length === 0 && !creating ? (
        <div className="text-center py-16 text-sm" style={{ color: "#6c7189" }}>لا توجد دعوات بعد — أنشئ أول دعوة وشارك رابطها مع المدعوين.</div>
      ) : (
        <div className="space-y-3" data-testid="invitations-list">
          {items.map((inv) => (
            <div key={inv.id} className="bg-white rounded-2xl border border-lav overflow-hidden" data-testid={`invitation-row-${inv.id}`}>
              <div className="p-5 flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-serif text-lg font-semibold" style={{ color: "#232a44" }}>{inv.title}</h3>
                    <span className="text-[11px] rounded-full bg-lavender px-3 py-1" style={{ color: "#3a4a78" }}>{inv.allowed_guests} مدعو/بطاقة</span>
                  </div>
                  {inv.event_date && <div className="text-sm mt-1" style={{ color: "#6c7189" }}>{new Date(inv.event_date).toLocaleString("ar", { dateStyle: "medium", timeStyle: "short" })} · {inv.venue_name}</div>}
                  <div className="flex items-center gap-4 mt-3 text-sm">
                    <span className="flex items-center gap-1" style={{ color: "#16a34a" }}><Check className="w-4 h-4" /> {inv.stats?.accepted ?? 0} مؤكّد</span>
                    <span className="flex items-center gap-1" style={{ color: "#6c7189" }}><Users className="w-4 h-4" /> {inv.stats?.head_count ?? 0} حضور</span>
                    <span style={{ color: "#dc2626" }}>{inv.stats?.declined ?? 0} اعتذار</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => copyLink(inv.id)} className="flex items-center gap-1.5 rounded-lg gradient-primary text-white px-4 py-2 text-sm" data-testid={`copy-invite-link-${inv.id}`}><Copy className="w-4 h-4" /> نسخ الرابط</button>
                  <button onClick={() => exportCsv(inv)} className="flex items-center gap-1.5 rounded-lg bg-lavender text-primary px-4 py-2 text-sm" data-testid={`export-attendance-${inv.id}`}><Download className="w-4 h-4" /> تصدير الحضور</button>
                  <button onClick={() => toggle(inv.id)} className="w-9 h-9 rounded-lg bg-lavender flex items-center justify-center text-primary" data-testid={`toggle-rsvps-${inv.id}`}><ChevronDown className={`w-4 h-4 transition-transform ${expanded === inv.id ? "rotate-180" : ""}`} /></button>
                  <button onClick={() => remove(inv.id)} className="w-9 h-9 rounded-lg bg-red-50 text-red-500 flex items-center justify-center" data-testid={`delete-invitation-${inv.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {expanded === inv.id && (
                <div className="border-t border-lav bg-lavender/40 p-5" data-testid={`rsvps-${inv.id}`}>
                  {(rsvps[inv.id] || []).length === 0 ? (
                    <div className="text-sm text-center py-2" style={{ color: "#6c7189" }}>لا توجد ردود بعد.</div>
                  ) : (
                    <div className="space-y-2">
                      {(rsvps[inv.id] || []).map((r) => (
                        <div key={r.id} className="flex items-center justify-between bg-white rounded-xl px-4 py-2.5 text-sm" data-testid={`rsvp-row-${r.id}`}>
                          <span className="font-medium" style={{ color: "#232a44" }}>{r.guest_name}</span>
                          <div className="flex items-center gap-3">
                            {r.status === "accepted" ? (
                              <span className="flex items-center gap-1" style={{ color: "#16a34a" }}><Check className="w-3.5 h-3.5" /> {r.guests_count} حضور</span>
                            ) : (
                              <span style={{ color: "#dc2626" }}>اعتذر</span>
                            )}
                            {r.pass_code && <span className="text-xs tracking-widest rounded bg-lavender px-2 py-0.5" style={{ color: "#3a4a78" }}>{r.pass_code}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReceptionTab() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");

  const extractCode = (text) => {
    try { const u = new URL(text); const p = u.pathname.split("/").filter(Boolean); return p[p.length - 1]; }
    catch { return String(text).trim(); }
  };

  const stop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setScanning(false);
  };

  const start = async () => {
    try {
      const jsQR = (await import("jsqr")).default;
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      const video = videoRef.current;
      video.srcObject = stream;
      video.setAttribute("playsinline", "true");
      await video.play();
      setScanning(true);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      const tick = () => {
        if (!streamRef.current || !videoRef.current) return;
        try {
          if (video.readyState === video.HAVE_ENOUGH_DATA) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const result = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" });
            if (result && result.data) {
              const code = extractCode(result.data);
              stop();
              navigate(`/verify/${code}`);
              return;
            }
          }
        } catch {}
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch {
      toast.error("تعذّر الوصول للكاميرا — تأكد من منح الإذن أو استخدم الإدخال اليدوي");
    }
  };

  useEffect(() => () => stop(), []);

  const goManual = () => {
    if (!manual.trim()) return toast.error("أدخل رمز البطاقة");
    navigate(`/verify/${manual.trim().toUpperCase()}`);
  };

  return (
    <div>
      <SectionTitle>الاستقبال والتحقق من البطاقات</SectionTitle>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-lav p-6" data-testid="scanner-panel">
          <div className="flex items-center gap-2 mb-4">
            <Camera className="w-5 h-5 text-primary" />
            <h3 className="font-semibold" style={{ color: "#232a44" }}>ماسح الكاميرا</h3>
          </div>
          <div className="relative rounded-xl overflow-hidden bg-navy aspect-video flex items-center justify-center">
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
            <canvas ref={canvasRef} className="hidden" />
            {!scanning && <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm px-4 text-center">اضغط "بدء المسح" ووجّه الكاميرا لرمز QR</div>}
            {scanning && <div className="absolute inset-8 border-2 border-gold rounded-xl pointer-events-none" />}
          </div>
          <div className="flex gap-3 mt-4">
            {!scanning ? (
              <button onClick={start} className="flex-1 flex items-center justify-center gap-2 rounded-xl gradient-primary text-white py-3 font-medium" data-testid="start-scan-btn"><ScanLine className="w-4 h-4" /> بدء المسح</button>
            ) : (
              <button onClick={stop} className="flex-1 flex items-center justify-center gap-2 rounded-xl border border-lav py-3 font-medium" style={{ color: "#6c7189" }} data-testid="stop-scan-btn"><X className="w-4 h-4" /> إيقاف</button>
            )}
          </div>
          <p className="text-xs mt-3" style={{ color: "#6c7189" }}>يعمل الماسح على جميع الأجهزة (آيفون وأندرويد). إن لم تُتَح الكاميرا، استخدم الإدخال اليدوي.</p>
        </div>

        <div className="bg-white rounded-2xl border border-lav p-6">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="w-5 h-5 text-primary" />
            <h3 className="font-semibold" style={{ color: "#232a44" }}>إدخال الرمز يدويًا</h3>
          </div>
          <p className="text-sm mb-4" style={{ color: "#6c7189" }}>اكتب رمز البطاقة الظاهر أسفل رمز QR للتحقق من الضيف وتسجيل دخوله.</p>
          <input value={manual} onChange={(e) => setManual(e.target.value)} onKeyDown={(e) => e.key === "Enter" && goManual()} placeholder="مثال: CC122982" className="w-full rounded-xl border border-lav px-4 py-3 text-center tracking-widest uppercase outline-none focus:border-primary mb-4" style={{ color: "#232a44" }} data-testid="manual-code-input" />
          <button onClick={goManual} className="w-full rounded-xl gradient-primary text-white py-3 font-semibold" data-testid="manual-verify-btn">تحقق من البطاقة</button>
          <div className="mt-5 rounded-xl bg-lavender p-4 text-xs leading-relaxed" style={{ color: "#3a4a78" }}>
            💡 طريقة بديلة سريعة: اطلب من الضيف عرض بطاقته، وامسح رمز QR بكاميرا جوالك مباشرة — ستفتح صفحة التحقق تلقائيًا.
          </div>
        </div>
      </div>
    </div>
  );
}

